import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import { Alert } from '@neo/primitives/alert'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@neo/primitives/sheet'
import { Skeleton } from '@neo/primitives/skeleton'
import { AlertTriangle, Info } from 'lucide-react'
import { formatCurrency } from 'v2/shared/utils'
import { Currency } from '../billing.config'
import { INR_BANK_TRANSFER_MIN_AMOUNT } from '../recharge-shared'

// Shared, gateway-agnostic presentational view for the bank-transfer (virtual
// account) details drawer. Both the Cashfree and Razorpay drawers render this;
// each supplies the normalized account details + owns its own data fetching,
// polling and credit-detection. Keeping the UI here is the single source of
// truth so the two gateways can't drift.
interface BankTransferDrawerViewProps {
  open: boolean
  onClose: () => void
  isLoading: boolean
  // Normalized from the gateway's vbank shape. When absent (and not loading) the
  // drawer shows the error state — i.e. no active virtual account to display.
  accountNumber?: string
  ifscCode?: string
  bankName?: string
}

const TRANSFER_GUIDELINES = [
  {
    step: 1,
    title: 'Log In to Your Bank Account',
    description: "Log in to your own bank's online banking portal or mobile app.",
  },
  {
    step: 2,
    title: 'Initiate a New Bank Transfer',
    description:
      'Choose IMPS, NEFT, or RTGS depending on amount and required speed (IMPS/RTGS settle in minutes; NEFT can take up to 2 business hours).',
  },
  {
    step: 3,
    title: 'Enter Virtual Bank Details',
    description:
      'Use the Account Number, IFSC Code, and Bank Name shown here. Beneficiary registration is one-time on most banks.',
  },
  {
    step: 4,
    title: 'Confirm and Send Funds',
    description:
      "Enter the desired transfer amount, review carefully, and confirm. You'll be notified once funds are received. For any issues, please contact",
  },
]

const SUPPORTED_METHODS = ['NEFT', 'IMPS', 'RTGS']

export const BankTransferDrawerView = ({
  open,
  onClose,
  isLoading,
  accountNumber,
  ifscCode,
  bankName,
}: BankTransferDrawerViewProps) => {
  const renderContent = () => {
    if (isLoading) return <LoadingSkeleton />
    if (!accountNumber) return <ErrorState />
    return (
      <>
        <VBankDetailsSection
          accountNumber={accountNumber}
          ifscCode={ifscCode}
          bankName={bankName}
        />
        <SupportedMethodsRow />
        <InfoBox />
        <SettlementBanner />
        <GuidelinesSection />
      </>
    )
  }

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:w-[550px] sm:max-w-none overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Details for Bank transfer</SheetTitle>
        </SheetHeader>
        <div className="mt-6 flex flex-col gap-6">{renderContent()}</div>
      </SheetContent>
    </Sheet>
  )
}

const VBankDetailsSection = ({
  accountNumber,
  ifscCode,
  bankName,
}: {
  accountNumber: string
  ifscCode?: string
  bankName?: string
}) => (
  <div className="flex flex-col gap-3 rounded border border-border p-3">
    <DetailRow label="Account Number" value={accountNumber} copyable copyTestId="account-number" />
    {ifscCode && <DetailRow label="IFSC Code" value={ifscCode} copyable copyTestId="ifsc-code" />}
    {bankName && <DetailRow label="Bank Name" value={bankName} />}
  </div>
)

const SupportedMethodsRow = () => (
  <div className="flex flex-col gap-2">
    <span className="text-s-400 text-muted-foreground">Supported Methods</span>
    <div className="flex gap-2">
      {SUPPORTED_METHODS.map((m) => (
        <span
          key={m}
          className="rounded border border-border px-2 py-0.5 font-jetbrains text-xs-400 text-muted-foreground"
        >
          {m}
        </span>
      ))}
    </div>
  </div>
)

const InfoBox = () => (
  <Alert
    variant="info"
    content={
      <p className="text-s-400">
        Transfer a minimum of{' '}
        <span className="text-s-500">
          {formatCurrency(INR_BANK_TRANSFER_MIN_AMOUNT, Currency.INR, false)}
        </span>{' '}
        from your bank. Your Plivo balance will be credited automatically once we receive the funds.
        No additional fees apply.
      </p>
    }
  />
)
const SettlementBanner = () => (
  <div className="flex items-start gap-2 rounded-md border border-status-warning/25 bg-status-warning/[0.08] p-3">
    <Info className="mt-0.5 h-4 w-4 shrink-0 text-status-warning" />
    <p className="text-s-400 text-muted-foreground">
      IMPS/RTGS: credited within minutes. NEFT: up to 2 hours during business hours.
    </p>
  </div>
)

const GuidelinesSection = () => (
  <div className="flex flex-col gap-4">
    <h3 className="font-inter text-p-500">Guidelines for funds transfer</h3>
    {TRANSFER_GUIDELINES.map((g) => (
      <div key={g.step} className="flex flex-col gap-1">
        <h4 className="text-p-500">
          Step {g.step}: {g.title}
        </h4>
        <p className="text-s-400 text-muted-foreground">
          {g.description}
          {g.step === 4 && (
            <>
              {' '}
              <a
                href="https://support.plivo.com/hc/en-us"
                target="_blank"
                rel="noopener noreferrer"
                className="text-link underline"
              >
                support team
              </a>
              .
            </>
          )}
        </p>
      </div>
    ))}
  </div>
)

const SKELETON_KEYS = ['s1', 's2', 's3', 's4'] as const

const LoadingSkeleton = () => (
  <div className="flex flex-col gap-3">
    {SKELETON_KEYS.map((key) => (
      <div key={key} className="grid grid-cols-[140px_1fr] gap-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-48" />
      </div>
    ))}
  </div>
)

const ErrorState = () => (
  <div className="flex flex-col items-center justify-center gap-2 py-8">
    <AlertTriangle className="h-8 w-8 text-muted-foreground" />
    <p className="text-s-400 text-muted-foreground">Unable to load virtual bank details</p>
  </div>
)

const DetailRow = ({
  label,
  value,
  copyable,
  copyTestId,
}: {
  label: string
  value: string
  copyable?: boolean
  copyTestId?: string
}) => (
  <div className="grid grid-cols-[140px_1fr] gap-2">
    <span className="font-jetbrains text-label uppercase text-muted-foreground">{label}</span>
    <div className="flex items-center gap-1 text-s-400">
      <span>{value}</span>
      {copyable && (
        <ClipboardCopyButton
          textToCopy={value}
          tooltipText={`Copy ${label}`}
          size="icon"
          data-testid={copyTestId ? `button-copy-${copyTestId}` : 'button-copy'}
        />
      )}
    </div>
  </div>
)
